import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import ReceiptOutlinedIcon from "@mui/icons-material/ReceiptOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import InputBase from "@mui/material/InputBase";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { neutral, primary } from "app/themes/colors";
import { QUICK_LAUNCHER_FEATURE } from "constants/feature-flags";
import { selectCurrentPractice } from "features/auth/auth-slice";
import { PrivacyModeProvider } from "hooks/use-patient-privacy.hook";
import useUserRoles from "hooks/use-user-roles.hook";

import useQuickLauncherDestinations from "./destinations/use-quick-launcher-destinations.hook";
import filterActions from "./filter-actions.util";
import KeyCap from "./key-cap.component";
import QuickLauncherHosts from "./quick-launcher-hosts.component";
import QuickLauncherPaper, {
  DRAG_HANDLE_CLASS
} from "./quick-launcher-paper.component";
import {
  QuickLauncherRunMethod,
  trackQuickLauncherOpened,
  trackQuickLauncherUsed
} from "./quick-launcher-telemetry";
import {
  closeQuickLauncher,
  selectQuickLauncherOpen,
  selectQuickLauncherOpenTrigger
} from "./quick-launcher.slice";
import {
  GROUP_ORDER,
  QuickLauncherAction,
  QuickLauncherActionGroup
} from "./quick-launcher.types";
import { SCREENSHOT_BLOCKING_ATTRIBUTE } from "./screenshot/screenshot-ignore.const";
import useQuickLauncherActions from "./use-quick-launcher-actions.hook";
import useQuickLauncherContextActions from "./use-quick-launcher-context-actions.hook";
import useQuickLauncherContext, {
  QuickLauncherContext
} from "./use-quick-launcher-context.hook";
import useQuickLauncherHotkey, {
  getQuickLauncherHotkeyLabel
} from "./use-quick-launcher-hotkey.hook";
import useQuickLauncherPatients, {
  MIN_PATIENT_SEARCH_LENGTH
} from "./use-quick-launcher-patients.hook";
import useQuickLauncherPractices from "./use-quick-launcher-practices.hook";

const LIST_MAX_HEIGHT = 384;

const NO_CONTEXT_ACTIONS: QuickLauncherAction[] = [];

/**
 * ⌘K / Ctrl+K command palette for the main app, gated on the `quickLauncher`
 * flag.
 *
 * Mounted once from `Layout`, inside the privacy-mode and softphone providers
 * (the context chip honours privacy mode; "Call patient" needs the softphone).
 * The "This patient" / "This invoice" sections also need the drawer providers
 * `routes.tsx` mounts above `Layout`, which is why Administration mounts
 * `AdministrationQuickLauncher` instead of this.
 */
export default function QuickLauncher() {
  const isEnabled = Boolean(useFlags()[QUICK_LAUNCHER_FEATURE]);
  const context = useQuickLauncherContext(isEnabled);
  const contextActions = useQuickLauncherContextActions(context);

  return (
    <QuickLauncherPalette
      isEnabled={isEnabled}
      context={context}
      contextActions={contextActions}
      hasPatientSearch
    />
  );
}

/**
 * The same palette for the Administration shell, which is its own route
 * outside `Layout` and the providers above it. No administration route is a
 * patient chart or a sale, so there is no context to act on, and its top bar
 * has no patient search box to focus.
 *
 * The privacy-mode provider is mounted here because the screenshot dialog
 * honours it and `Layout`'s copy is not an ancestor of this shell.
 */
export function AdministrationQuickLauncher() {
  const isEnabled = Boolean(useFlags()[QUICK_LAUNCHER_FEATURE]);

  return (
    <PrivacyModeProvider>
      <QuickLauncherPalette
        isEnabled={isEnabled}
        context={null}
        contextActions={NO_CONTEXT_ACTIONS}
        hasPatientSearch={false}
      />
    </PrivacyModeProvider>
  );
}

type QuickLauncherPaletteProps = {
  isEnabled: boolean;
  context: QuickLauncherContext | null;
  contextActions: QuickLauncherAction[];
  hasPatientSearch: boolean;
};

/**
 * The palette both shells share. It owns its query, highlight and nothing
 * else — entries come from `useQuickLauncherActions` and
 * `useQuickLauncherPatients`, open/closed state and the drag offset live in
 * Redux so the window-level hotkey and the draggable Paper can both drive it.
 *
 * Who sees the launcher is the flag's decision; which entries a user gets is
 * `useQuickLauncherActions`' (`sunoOnly` for the internal group).
 */
function QuickLauncherPalette({
  isEnabled,
  context,
  contextActions,
  hasPatientSearch
}: QuickLauncherPaletteProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isSunoAdmin } = useUserRoles();
  const open = useAppSelector(selectQuickLauncherOpen);
  const openTrigger = useAppSelector(selectQuickLauncherOpenTrigger);
  const currentPractice = useAppSelector(selectCurrentPractice);
  const actions = useQuickLauncherActions(contextActions, { hasPatientSearch });

  // Bound only for the users who can actually open the launcher: ⌘K / Ctrl+K is
  // Chrome's "focus the address bar", and claiming it from someone who has no
  // launcher would be a pure regression.
  useQuickLauncherHotkey(isEnabled);

  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);
  // Suppresses the hover highlight for one frame after an arrow-key move:
  // scrolling a row under a stationary cursor fires `mouseenter`, which would
  // yank the highlight back to wherever the pointer happens to sit.
  const isKeyboardNavigatingRef = useRef(false);

  const { patientActions, isSearching: isSearchingPatients } =
    useQuickLauncherPatients(query, open && isEnabled);
  const { practiceActions, isSearching: isSearchingPractices } =
    useQuickLauncherPractices(query, open && isEnabled);
  const isSearching = isSearchingPatients || isSearchingPractices;

  const destinationActions = useQuickLauncherDestinations(open && isEnabled);

  const rankedActions = useMemo(
    () => filterActions([...actions, ...destinationActions], query),
    [actions, destinationActions, query]
  );

  /**
   * Sections in GROUP_ORDER, each row numbered as the list is built.
   *
   * The numbering has to happen here rather than over the ranked array: ranking
   * is global but rendering is grouped, so a top-ranked Navigate row can be
   * drawn *below* a lower-ranked Action. Indexing the ranked array directly made
   * ArrowDown appear to jump backwards. Assigning indices in render order keeps
   * "row 3" meaning the same thing to the keyboard and to the eye.
   */
  const sections = useMemo(() => {
    let cursor = 0;
    return GROUP_ORDER.map((group: QuickLauncherActionGroup) => {
      // Patient and practice rows are already server-matched; see
      // `useQuickLauncherPatients` for why they must not go through `filterActions`.
      const source =
        group === "Patients"
          ? patientActions
          : group === "Practices"
            ? practiceActions
            : rankedActions.filter((action) => action.group === group);
      return {
        group,
        items: source.map((action) => ({ action, index: cursor++ }))
      };
    }).filter(({ items }) => items.length > 0);
  }, [rankedActions, patientActions, practiceActions]);

  // Flat, in render order — the array the keyboard walks.
  const orderedResults = useMemo(
    () => sections.flatMap(({ items }) => items.map(({ action }) => action)),
    [sections]
  );

  useEffect(() => {
    if (open) {
      setQuery("");
      setHighlightedIndex(0);
    }
  }, [open]);

  // One "opened" event per opening. `context?.type` is read through a ref so a
  // patient fetch resolving mid-session cannot re-fire this; the event answers
  // "where was the user when they pressed ⌘K", which the URL already knows.
  const contextTypeRef = useRef(context?.type ?? null);
  contextTypeRef.current = context?.type ?? null;
  useEffect(() => {
    if (!open) return;
    trackQuickLauncherOpened({
      trigger: openTrigger,
      contextType: contextTypeRef.current,
      internalUser: isSunoAdmin
    });
    // `openTrigger` is set in the same reducer that flips `open`, so it is never
    // a second trigger on its own; `isSunoAdmin` only changes when /me resolves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A shrinking result set can strand the highlight past the end.
  useEffect(() => {
    setHighlightedIndex((current) =>
      current > orderedResults.length - 1 ? 0 : current
    );
  }, [orderedResults.length]);

  // Keep the highlighted row in view. In an effect rather than in the state
  // updater so the updater stays pure (React may call it twice in StrictMode).
  // The optional call is not paranoia: jsdom does not implement
  // `scrollIntoView`, so a plain call throws in every unit test that renders
  // this list — and scrolling is cosmetic, so there is nothing to fall back to.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector(`[data-index="${highlightedIndex}"]`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [highlightedIndex, open]);

  const handleClose = useCallback(() => {
    dispatch(closeQuickLauncher());
  }, [dispatch]);

  const runAction = useCallback(
    (action: QuickLauncherAction, method: QuickLauncherRunMethod) => {
      trackQuickLauncherUsed({
        actionId: action.telemetryId ?? action.id,
        group: action.group,
        method,
        contextType: contextTypeRef.current,
        queryLength: query.trim().length,
        internalUser: isSunoAdmin
      });
      // Close first: `perform` usually navigates, and leaving the dialog mounted
      // across a route transition strands focus in a detached tree.
      dispatch(closeQuickLauncher());
      action.perform();
    },
    [dispatch, query, isSunoAdmin]
  );

  const moveHighlight = useCallback(
    (delta: number) => {
      const count = orderedResults.length;
      if (count === 0) return;
      isKeyboardNavigatingRef.current = true;
      setHighlightedIndex((current) => (current + delta + count) % count);
      // One frame is enough for the scroll-induced mouseenter to land.
      window.requestAnimationFrame(() => {
        isKeyboardNavigatingRef.current = false;
      });
    },
    [orderedResults.length]
  );

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveHighlight(1);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveHighlight(-1);
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const action = orderedResults[highlightedIndex];
      if (action) runAction(action, "keyboard");
    }
  };

  // Flag off => nothing at all. Checked after every hook, so hook order stays
  // stable across the moment LaunchDarkly resolves and the flag flips on.
  if (!isEnabled) return null;

  const trimmedQuery = query.trim();
  const canSearchPatients = trimmedQuery.length >= MIN_PATIENT_SEARCH_LENGTH;

  const emptyStateMessage = canSearchPatients
    ? `No actions, pages${isSunoAdmin ? ", practices" : ""} or patients match “${trimmedQuery}”`
    : `No matches for “${trimmedQuery}” — type ${MIN_PATIENT_SEARCH_LENGTH} or more characters to search patients`;

  // Names the record the context sections act on. On a sale route the invoice
  // comes first and the patient second, since the "This invoice" section sits
  // at the top of the list.
  const contextLabel = context
    ? context.type === "sale"
      ? `Invoice #${context.saleId} · ${context.patientLabel}`
      : context.patientLabel
    : null;
  const ContextIcon =
    context?.type === "sale" ? ReceiptOutlinedIcon : PersonOutlineOutlinedIcon;

  return (
    <>
      {/* The dialogs the launcher can open (appointment form, new alert) live
          outside its own Dialog so they outlive it closing. */}
      <QuickLauncherHosts />
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        // Keeps the launcher out of "Screenshot this page". The capture is the
        // browser's own pixels, so there is nothing to filter — `capturePage`
        // instead waits until no element carrying this attribute is in the
        // document, which covers the exit transition `perform` runs during.
        // MUI spreads unknown props onto the Dialog root, the node that also
        // parents the backdrop.
        {...{ [SCREENSHOT_BLOCKING_ATTRIBUTE]: "" }}
        // Anchored high rather than centred, so the input stays put under the
        // cursor while the list below it grows and shrinks. `flex-start` on both
        // axes also gives react-draggable a stable origin to offset from.
        sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
        PaperComponent={QuickLauncherPaper}
        PaperProps={{
          "data-testid": "quick-launcher",
          sx: {
            mt: 12,
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: theme.shadows[8]
          }
        }}
      >
        <Box onKeyDown={handleKeyDown}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            className={DRAG_HANDLE_CLASS}
            data-testid="quick-launcher-drag-handle"
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              cursor: "move",
              // The grip only appears on hover of the row itself: it is an
              // affordance for a secondary gesture, and a permanently visible
              // handle competes with the search icon for the same glance.
              "&:hover .quick-launcher-grip": { opacity: 1 }
            }}
          >
            <SearchIcon sx={{ color: neutral[600] }} />
            <InputBase
              autoFocus
              fullWidth
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setHighlightedIndex(0);
              }}
              placeholder="Search actions, pages and patients…"
              inputProps={{
                "aria-label": "Search actions, pages and patients",
                "data-testid": "quick-launcher-input",
                role: "combobox",
                "aria-expanded": true,
                "aria-controls": "quick-launcher-list",
                "aria-activedescendant":
                  orderedResults.length > 0
                    ? `quick-launcher-option-${highlightedIndex}`
                    : undefined
              }}
              // The input is in react-draggable's `cancel` list, so typing and
              // text selection are unaffected by the handle around it.
              sx={{ fontSize: 16, cursor: "text" }}
            />
            {isSearching && (
              <CircularProgress
                size={16}
                data-testid="quick-launcher-searching"
                aria-label="Searching"
              />
            )}
            <DragIndicatorIcon
              className="quick-launcher-grip"
              aria-hidden
              sx={{
                color: neutral[400],
                fontSize: 18,
                opacity: 0,
                transition: "opacity 120ms"
              }}
            />
            <KeyCap>esc</KeyCap>
          </Stack>

          {/* Context readout: its own row under the search so the input keeps
              its full width. A subtitle, not a control — it names the record the
              "This patient" / "This invoice" sections act on. */}
          {contextLabel && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.75}
              data-testid="quick-launcher-context"
              sx={{
                px: 2,
                py: 0.75,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(primary[400], 0.06),
                color: primary[500]
              }}
            >
              <ContextIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
                {contextLabel}
              </Typography>
            </Stack>
          )}

          {orderedResults.length === 0 ? (
            <Box sx={{ px: 2, py: 5, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {isSearching ? "Searching…" : emptyStateMessage}
              </Typography>
            </Box>
          ) : (
            <List
              ref={listRef}
              id="quick-launcher-list"
              role="listbox"
              aria-label="Quick launcher actions"
              dense
              sx={{ maxHeight: LIST_MAX_HEIGHT, overflowY: "auto", py: 1 }}
            >
              {sections.map(({ group, items }) => (
                <Box
                  key={group}
                  component="li"
                  role="group"
                  aria-label={group}
                  sx={{ listStyle: "none" }}
                >
                  <Typography
                    variant="caption"
                    aria-hidden
                    sx={{
                      display: "block",
                      px: 2,
                      py: 0.5,
                      color: neutral[600],
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase"
                    }}
                  >
                    {group}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
                    {items.map(({ action, index }) => {
                      const isHighlighted = index === highlightedIndex;
                      const { Icon } = action;
                      return (
                        <ListItemButton
                          key={action.id}
                          component="li"
                          id={`quick-launcher-option-${index}`}
                          data-index={index}
                          data-testid={`quick-launcher-option-${action.id}`}
                          role="option"
                          aria-selected={isHighlighted}
                          selected={isHighlighted}
                          onMouseEnter={() => {
                            if (isKeyboardNavigatingRef.current) return;
                            setHighlightedIndex(index);
                          }}
                          onClick={() => runAction(action, "click")}
                          sx={{
                            alignItems: "center",
                            gap: 1.5,
                            mx: 1,
                            px: 1.5,
                            py: 1,
                            borderRadius: 2,
                            "&.Mui-selected, &.Mui-selected:hover": {
                              backgroundColor: alpha(primary[400], 0.12)
                            }
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              backgroundColor: isHighlighted
                                ? alpha(primary[400], 0.18)
                                : neutral[100],
                              color: isHighlighted ? primary[500] : neutral[700]
                            }}
                          >
                            <Icon fontSize="small" />
                          </Box>
                          <Stack sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              color="text.primary"
                            >
                              {action.label}
                            </Typography>
                            {action.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                              >
                                {action.description}
                              </Typography>
                            )}
                          </Stack>
                          {action.trailingLabel && (
                            <Typography
                              variant="caption"
                              sx={{ flexShrink: 0, color: neutral[600] }}
                            >
                              {action.trailingLabel}
                            </Typography>
                          )}
                        </ListItemButton>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </List>
          )}

          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            sx={{
              px: 2,
              py: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: neutral[50]
            }}
          >
            <Stack direction="row" alignItems="center" gap={0.75}>
              <KeyCap>↑</KeyCap>
              <KeyCap>↓</KeyCap>
              <Typography variant="caption" color="text.secondary">
                navigate
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <KeyCap>↵</KeyCap>
              <Typography variant="caption" color="text.secondary">
                select
              </Typography>
            </Stack>
            <Box sx={{ flexGrow: 1 }} />
            {/* The banner that used to name the active practice can now be hidden,
              and nothing else in the shell reliably names it — so this readout is
              load-bearing, not decoration. See practice-context-banner. */}
            {currentPractice?.name && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                data-testid="quick-launcher-practice"
                sx={{ maxWidth: 220 }}
              >
                {currentPractice.name}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {getQuickLauncherHotkeyLabel()}
            </Typography>
          </Stack>
        </Box>
      </Dialog>
    </>
  );
}
